import { getReferenceString } from '@medplum/core';
import { Claim, ClaimResponse, Coverage, Extension, Patient } from '@medplum/fhirtypes';
import { BenefitDecision, CoverageAction, PRIOR_AUTH_PREP_ITEMS, PriorAuthRequirement, Track } from './constants';
import { BENEFIT_DECISION_EXTENSION, COVERAGE_ACTION_EXTENSION, PAYMENT_STATUS_EXTENSION } from './extensions';

/**
 * Payment status (BAR-3081): the patient's latest step toward Everself being paid, one ladder per
 * track. Derived from recorded events, never hand-set. Spec: design-system `payment-status.mdx`.
 * To invoice / Invoiced / Paid arrive with Finance Ops receivables.
 */
export enum PaymentStatus {
  NOT_STARTED = 'not-started',
  PAYMENT_REQUESTED = 'payment-requested',
  PARTIALLY_PAID = 'partially-paid',
  REFERRAL_SENT = 'referral-sent',
  BENEFIT_CONFIRMED = 'benefit-confirmed',
  CASE_FORM_SUBMITTED = 'case-form-submitted',
  PRIOR_AUTH_PREP = 'prior-auth-prep',
  PRIOR_AUTH_READY = 'prior-auth-ready',
  PRIOR_AUTH_SUBMITTED = 'prior-auth-submitted',
  READY_TO_SCHEDULE = 'ready-to-schedule',
  WON = 'won',
  NO_BENEFIT = 'no-benefit',
  APPROVAL_DENIED = 'approval-denied',
  PRIOR_AUTH_DENIED = 'prior-auth-denied',
  CANCELLED_AFTER_APPROVAL = 'cancelled-after-approval',
  REFUND_REQUESTED = 'refund-requested',
  REFUND_APPROVED = 'refund-approved',
  REFUNDED = 'refunded',
}

export const PaymentStatusLabel: Record<PaymentStatus, string> = {
  [PaymentStatus.NOT_STARTED]: 'Not started',
  [PaymentStatus.PAYMENT_REQUESTED]: 'Payment requested',
  [PaymentStatus.PARTIALLY_PAID]: 'Partially paid',
  [PaymentStatus.REFERRAL_SENT]: 'Referral sent',
  [PaymentStatus.BENEFIT_CONFIRMED]: 'Benefit confirmed',
  [PaymentStatus.CASE_FORM_SUBMITTED]: 'Case form submitted',
  [PaymentStatus.PRIOR_AUTH_PREP]: 'Prior auth prep',
  [PaymentStatus.PRIOR_AUTH_READY]: 'Prior auth ready',
  [PaymentStatus.PRIOR_AUTH_SUBMITTED]: 'Prior auth submitted',
  [PaymentStatus.READY_TO_SCHEDULE]: 'Ready to schedule',
  [PaymentStatus.WON]: 'Won',
  [PaymentStatus.NO_BENEFIT]: 'No benefit',
  [PaymentStatus.APPROVAL_DENIED]: 'Approval denied',
  [PaymentStatus.PRIOR_AUTH_DENIED]: 'Prior auth denied',
  [PaymentStatus.CANCELLED_AFTER_APPROVAL]: 'Cancelled after approval',
  [PaymentStatus.REFUND_REQUESTED]: 'Refund requested',
  [PaymentStatus.REFUND_APPROVED]: 'Refund approved',
  [PaymentStatus.REFUNDED]: 'Refunded',
};

/** The Status badge colour: neutral started, info waiting on someone else, warning our move, success done, error a no. */
export type PaymentStatusTone = 'neutral' | 'info' | 'warning' | 'success' | 'error';

export const PaymentStatusTone: Record<PaymentStatus, PaymentStatusTone> = {
  [PaymentStatus.NOT_STARTED]: 'neutral',
  [PaymentStatus.PAYMENT_REQUESTED]: 'info',
  [PaymentStatus.PARTIALLY_PAID]: 'info',
  [PaymentStatus.REFERRAL_SENT]: 'info',
  [PaymentStatus.BENEFIT_CONFIRMED]: 'warning',
  [PaymentStatus.CASE_FORM_SUBMITTED]: 'info',
  [PaymentStatus.PRIOR_AUTH_PREP]: 'info',
  [PaymentStatus.PRIOR_AUTH_READY]: 'warning',
  [PaymentStatus.PRIOR_AUTH_SUBMITTED]: 'info',
  [PaymentStatus.READY_TO_SCHEDULE]: 'warning',
  [PaymentStatus.WON]: 'success',
  [PaymentStatus.NO_BENEFIT]: 'error',
  [PaymentStatus.APPROVAL_DENIED]: 'error',
  [PaymentStatus.PRIOR_AUTH_DENIED]: 'error',
  [PaymentStatus.CANCELLED_AFTER_APPROVAL]: 'error',
  [PaymentStatus.REFUND_REQUESTED]: 'warning',
  [PaymentStatus.REFUND_APPROVED]: 'info',
  [PaymentStatus.REFUNDED]: 'error',
};

type LadderTrack = Track.D2C | Track.EMPLOYER | Track.INSURANCE;

/** Each track's Financing & approval statuses, in ladder order (`payment-status.mdx`). */
export const FINANCING_STEPS: Record<LadderTrack, PaymentStatus[]> = {
  [Track.D2C]: [PaymentStatus.PAYMENT_REQUESTED, PaymentStatus.PARTIALLY_PAID, PaymentStatus.READY_TO_SCHEDULE],
  [Track.EMPLOYER]: [
    PaymentStatus.NOT_STARTED,
    PaymentStatus.REFERRAL_SENT,
    PaymentStatus.BENEFIT_CONFIRMED,
    PaymentStatus.CASE_FORM_SUBMITTED,
    PaymentStatus.READY_TO_SCHEDULE,
  ],
  [Track.INSURANCE]: [
    PaymentStatus.NOT_STARTED,
    PaymentStatus.PRIOR_AUTH_PREP,
    PaymentStatus.PRIOR_AUTH_READY,
    PaymentStatus.PRIOR_AUTH_SUBMITTED,
    PaymentStatus.PRIOR_AUTH_DENIED,
    PaymentStatus.READY_TO_SCHEDULE,
  ],
};

const LADDER_TRACK: Record<Track, LadderTrack> = {
  [Track.D2C]: Track.D2C,
  [Track.EMPLOYER]: Track.EMPLOYER,
  [Track.CARRUM]: Track.EMPLOYER,
  [Track.TRANSCARENT]: Track.EMPLOYER,
  [Track.INSURANCE]: Track.INSURANCE,
};

/** The Step filter's options for these tracks (BAR-3094): ladder order, each status once. */
export function financingStepOptions(
  tracks: Track[],
  options: { withoutReadyToSchedule?: boolean } = {}
): { value: PaymentStatus; label: string }[] {
  const steps = [...new Set(tracks.flatMap((track) => FINANCING_STEPS[LADDER_TRACK[track]]))].filter(
    (status) => !(options.withoutReadyToSchedule && status === PaymentStatus.READY_TO_SCHEDULE)
  );
  return steps.map((status) => ({ value: status, label: PaymentStatusLabel[status] }));
}

/**
 * The statuses a cancelling Refund or cancellation request leads to. The patient's Lead status is
 * Cancelled from the moment one is created, without waiting for Finance (`patient-profile.mdx`).
 */
const CANCELLED_STATUSES: ReadonlySet<PaymentStatus> = new Set([
  PaymentStatus.CANCELLED_AFTER_APPROVAL,
  PaymentStatus.REFUND_REQUESTED,
  PaymentStatus.REFUND_APPROVED,
  PaymentStatus.REFUNDED,
]);

export function isCancelledPaymentStatus(status: PaymentStatus | undefined): boolean {
  return status !== undefined && CANCELLED_STATUSES.has(status);
}

export interface RecordedCoverageAction {
  action: CoverageAction;
  /** YYYY-MM-DD, operator-entered: the day the step happened. */
  on: string;
  /** `meta.lastUpdated`. The latest recorded action wins, as in `getCurrentBenefitDecision`. */
  recordedAt: string;
}

export interface PrepItem {
  code: PriorAuthRequirement;
  status: 'requested' | 'completed' | 'cancelled';
  /** When it was completed or cancelled. */
  at?: string;
}

/** A Won or its reversal, as the payments backend's Won history returns it (BAR-2944). */
export interface WonHistoryEvent {
  id: string;
  kind: 'WON' | 'REVERSAL';
  occurredAt: string;
  reversesId: string | null;
}

/** The standing Win: the latest WON no REVERSAL undoes. */
export function standingWonAt(events: WonHistoryEvent[]): string | undefined {
  const reversed = new Set(events.filter((e) => e.kind === 'REVERSAL').map((e) => e.reversesId));
  return events
    .filter((e) => e.kind === 'WON' && !reversed.has(e.id))
    .map((e) => e.occurredAt)
    .sort()
    .pop();
}

/** The parts of a refund request (payments backend, BAR-3052) the status reads. */
export interface PaymentStatusRefundRequest {
  requestedAt: string;
  cancel: boolean;
  amountRequested: number;
  approvedAt: string | null;
  linkedAt: string | null;
}

export interface PaymentStatusFacts {
  track: Track;
  payor?: string;
  /** The PN started the employer or insurance path (the coverage verification task). */
  pathStartedAt?: string;
  /** The open self-pay order. Dollars. */
  order?: { createdAt: string; amountPaid: number; balance: number; lastPaymentAt?: string };
  coverageActions: RecordedCoverageAction[];
  prepItems: PrepItem[];
  /** The standing Won; the caller nets out reversals. */
  wonAt?: string;
  refundRequests: PaymentStatusRefundRequest[];
}

export interface PaymentStatusResult {
  status: PaymentStatus;
  /** When it moved there. */
  at: string;
  /** Carrum, Transcarent or the insurer, on the coverage tracks. */
  payor?: string;
  /**
   * The track it was computed on, stored so an event read later names the track of the
   * time, not today's. Not compared by `samePaymentStatus`: a status stored before it
   * existed is not rewritten for it.
   */
  track?: Track;
}

const ACTION_STATUS: Partial<Record<CoverageAction, PaymentStatus>> = {
  [CoverageAction.REFERRAL_SENT]: PaymentStatus.REFERRAL_SENT,
  [CoverageAction.BENEFIT_CONFIRMED]: PaymentStatus.BENEFIT_CONFIRMED,
  [CoverageAction.CASE_FORM_SUBMITTED]: PaymentStatus.CASE_FORM_SUBMITTED,
  [CoverageAction.APPROVAL_DENIED]: PaymentStatus.APPROVAL_DENIED,
  [CoverageAction.PRIOR_AUTH_SUBMITTED]: PaymentStatus.PRIOR_AUTH_SUBMITTED,
  [CoverageAction.PRIOR_AUTH_DENIED]: PaymentStatus.PRIOR_AUTH_DENIED,
  [CoverageAction.NO_BENEFIT]: PaymentStatus.NO_BENEFIT,
  [CoverageAction.APPROVED]: PaymentStatus.READY_TO_SCHEDULE,
};

/** A set opened before the questionnaire (BAR-3128) asked for PCP clearance in its place. */
const LEGACY_PREP_ITEMS: readonly PriorAuthRequirement[] = [
  PriorAuthRequirement.RD_CLEARANCE,
  PriorAuthRequirement.NP_CLEARANCE,
  PriorAuthRequirement.PSYCH_EVALUATION,
  PriorAuthRequirement.PCP_CLEARANCE,
];
const CHECKLIST_ONLY_ACTIONS: ReadonlySet<CoverageAction> = new Set([
  CoverageAction.QUESTIONNAIRE_RECEIVED,
  CoverageAction.PSYCH_EVALUATION_RECEIVED,
  CoverageAction.PCP_CLEARANCE_RECEIVED,
]);
const COVERAGE_ACTIONS = new Set<string>(Object.values(CoverageAction));

function latest<T>(items: T[], at: (item: T) => string): T | undefined {
  return [...items].sort((a, b) => at(b).localeCompare(at(a)))[0];
}

/** A set with a PCP clearance item and no questionnaire was opened before BAR-3128. */
export function isPreQuestionnaireSet(items: PrepItem[]): boolean {
  const has = (code: PriorAuthRequirement): boolean => items.some((item) => item.code === code);
  return has(PriorAuthRequirement.PCP_CLEARANCE) && !has(PriorAuthRequirement.QUESTIONNAIRE);
}

/**
 * All four prep items done or waived: the date of the last one. A set opened before the
 * questionnaire is judged on the four it was opened with.
 */
export function prepDoneAt(items: PrepItem[]): string | undefined {
  const required = isPreQuestionnaireSet(items) ? LEGACY_PREP_ITEMS : PRIOR_AUTH_PREP_ITEMS;
  const done = required.map((code) => items.find((item) => item.code === code && item.status !== 'requested'));
  if (done.some((item) => !item)) {
    return undefined;
  }
  return latest(done as PrepItem[], (item) => item.at ?? '')?.at;
}

function fromRefundRequest(facts: PaymentStatusFacts, coverage: boolean): PaymentStatusResult | undefined {
  const request = latest(
    facts.refundRequests.filter((r) => r.cancel),
    (r) => r.requestedAt
  );
  if (!request) {
    return undefined;
  }
  if (coverage || request.amountRequested <= 0) {
    return { status: PaymentStatus.CANCELLED_AFTER_APPROVAL, at: request.requestedAt };
  }
  if (request.linkedAt) {
    return { status: PaymentStatus.REFUNDED, at: request.linkedAt };
  }
  if (request.approvedAt) {
    return { status: PaymentStatus.REFUND_APPROVED, at: request.approvedAt };
  }
  return { status: PaymentStatus.REFUND_REQUESTED, at: request.requestedAt };
}

function fromOrder(order: NonNullable<PaymentStatusFacts['order']>): PaymentStatusResult {
  const paidAt = order.lastPaymentAt ?? order.createdAt;
  if (order.amountPaid > 0 && order.balance <= 0) {
    return { status: PaymentStatus.READY_TO_SCHEDULE, at: paidAt };
  }
  if (order.amountPaid > 0) {
    return { status: PaymentStatus.PARTIALLY_PAID, at: paidAt };
  }
  return { status: PaymentStatus.PAYMENT_REQUESTED, at: order.createdAt };
}

function fromCoverageActions(facts: PaymentStatusFacts): Omit<PaymentStatusResult, 'payor'> | undefined {
  // The received steps only tick the checklist.
  const action = latest(
    facts.coverageActions.filter((a) => !CHECKLIST_ONLY_ACTIONS.has(a.action)),
    (a) => a.recordedAt
  );
  if (!action) {
    return undefined;
  }
  if (action.action === CoverageAction.BENEFIT_VERIFIED) {
    const readyAt = prepDoneAt(facts.prepItems);
    return readyAt
      ? { status: PaymentStatus.PRIOR_AUTH_READY, at: readyAt }
      : { status: PaymentStatus.PRIOR_AUTH_PREP, at: action.on };
  }
  const status = ACTION_STATUS[action.action];
  return status ? { status, at: action.on } : undefined;
}

/** The payment status, or undefined for a patient not on a payment path. Highest wins. */
export function derivePaymentStatus(facts: PaymentStatusFacts): PaymentStatusResult | undefined {
  const coverage = facts.track !== Track.D2C;
  const withPayor = (result: Omit<PaymentStatusResult, 'payor'> | undefined): PaymentStatusResult | undefined =>
    result && (coverage && facts.payor ? { ...result, payor: facts.payor } : result);

  const cancelled = fromRefundRequest(facts, coverage);
  if (cancelled) {
    return withPayor(cancelled);
  }
  if (facts.wonAt) {
    return withPayor({ status: PaymentStatus.WON, at: facts.wonAt });
  }
  if (!coverage) {
    return facts.order ? fromOrder(facts.order) : undefined;
  }
  const step = fromCoverageActions(facts);
  if (step) {
    return withPayor(step);
  }
  return facts.pathStartedAt ? withPayor({ status: PaymentStatus.NOT_STARTED, at: facts.pathStartedAt }) : undefined;
}

const PAYMENT_STATUSES = new Set<string>(Object.values(PaymentStatus));
const TRACKS = new Set<string>(Object.values(Track));

export function buildPaymentStatusExtension(result: PaymentStatusResult): Extension {
  return {
    url: PAYMENT_STATUS_EXTENSION,
    extension: [
      { url: 'status', valueCode: result.status },
      { url: 'at', valueDateTime: result.at },
      ...(result.payor ? [{ url: 'payor', valueString: result.payor }] : []),
      ...(result.track ? [{ url: 'track', valueCode: result.track }] : []),
    ],
  };
}

/** The payment status the stage bot stored on the Patient (BAR-3082). */
export function getStoredPaymentStatus(patient: Patient): PaymentStatusResult | undefined {
  const parts = patient.extension?.find((e) => e.url === PAYMENT_STATUS_EXTENSION)?.extension;
  const status = parts?.find((e) => e.url === 'status')?.valueCode;
  const at = parts?.find((e) => e.url === 'at')?.valueDateTime;
  if (!status || !PAYMENT_STATUSES.has(status) || !at) {
    return undefined;
  }
  const payor = parts?.find((e) => e.url === 'payor')?.valueString;
  const track = parts?.find((e) => e.url === 'track')?.valueCode;
  return {
    status: status as PaymentStatus,
    at,
    ...(payor ? { payor } : {}),
    ...(track && TRACKS.has(track) ? { track: track as Track } : {}),
  };
}

export function samePaymentStatus(a: PaymentStatusResult | undefined, b: PaymentStatusResult | undefined): boolean {
  return a?.status === b?.status && a?.at === b?.at && a?.payor === b?.payor;
}

/**
 * The coverage step a case resource records. Records from before BAR-3081 carry no action:
 * a Submitted Claim reads as Case form submitted, a decision as Approved or Approval denied.
 */
export function readCoverageAction(resource: Claim | ClaimResponse): CoverageAction | undefined {
  const code = resource.extension?.find((e) => e.url === COVERAGE_ACTION_EXTENSION)?.valueCoding?.code;
  if (code) {
    return COVERAGE_ACTIONS.has(code) ? (code as CoverageAction) : undefined;
  }
  if (resource.resourceType === 'Claim') {
    return resource.use === 'preauthorization' ? CoverageAction.CASE_FORM_SUBMITTED : undefined;
  }
  const decision = resource.extension?.find((e) => e.url === BENEFIT_DECISION_EXTENSION)?.valueCoding?.code;
  if (decision === BenefitDecision.APPROVED) {
    return CoverageAction.APPROVED;
  }
  if (decision === BenefitDecision.DENIED) {
    return CoverageAction.APPROVAL_DENIED;
  }
  return undefined;
}

/** The coverage steps recorded against the patient's active Coverage, from their case resources. */
export function recordedCoverageActions(
  resources: (Claim | ClaimResponse)[],
  coverage: Coverage | undefined
): RecordedCoverageAction[] {
  const coverageReference = coverage ? getReferenceString(coverage) : undefined;
  return resources.flatMap((resource) => {
    const onCoverage = resource.insurance?.some((insurance) => insurance.coverage?.reference === coverageReference);
    const action = readCoverageAction(resource);
    if (!coverageReference || !onCoverage || !action || !resource.created) {
      return [];
    }
    return [{ action, on: resource.created.slice(0, 10), recordedAt: resource.meta?.lastUpdated ?? resource.created }];
  });
}
